import Broadcast from "../models/Broadcast";
import Contact from "../models/Contacts";
import Groups from "../models/Groups";
import Tags from "../models/Tags";
import request from "../shared/http";
import appConfig from "../shared/js/app-config";

class BroadcastService {
  parse(item) {
    return {
      id: item.id,
      broadcastName: item.name,
      scheduleDate: item.schedule,
      selectedTemplate: item.template_id,
      selectedContacts: (item.selected_contacts || []).map((con) => new Contact()
                          .setId(con.id)
                          .setName(con.name)
                          .setPhoneNumber(con.phone_number)
                          .setEmail(con.email)),
      selectedGroups: (item.selected_groups || []).map((con) => new Groups()
                          .setId(con.id)
                          .setName(con.name)
                          .setContactsCount(con.contacts_count)
                          .setDescription(con.description)),
      selectedTags: (item.selected_tags || []).map((con) => {
        const tag = new Tags();
        tag.setId(con.id);
        tag.setName(con.name);
        if (con.contacts_count != null) tag.setContactsCount(con.contacts_count);
        if (con.description != null) tag.setDescription(con.description);
        return tag;
      }),
      templateVariables: item.template_variables,
      success: item.success,
      failed: item.failed,
      read: item.read,
      replied: item.replied,
      recipients: item.recipients,
      status: item.status,
      // Platform-event mapping fields. Null on legacy/manual broadcasts.
      triggerType: item.trigger_type ?? null,
      triggerPlatform: item.trigger_platform ?? null,
      triggerEvent: item.trigger_event ?? null,
      variablePaths: typeof item.variable_paths === "string"
        ? (() => { try { return JSON.parse(item.variable_paths); } catch { return null; } })()
        : (item.variable_paths ?? null),
      enabled: item.enabled === undefined ? null : Boolean(item.enabled),
    }
  }
  serialize(model) {
    const triggerType = model.getTriggerType?.();
    const isEventTriggered = triggerType === "platform_event";
    const base = {
      id: model.getId() ?? null,
      name: model.getBroadCastName(),
      send_now: model.getSendNow(),
      selected_contacts: model.getSelectedContacts()?.map((con) => con.attrs.id) ?? [],
      selected_groups: model.getSelectedGroups()?.map((con) => con.attrs.id) ?? [],
      selected_tags: model.getSelectedTags?.()?.map((tag) => tag.attrs.id) ?? [],
      selected_template: model.getSelectedTemplate(),
      schedule: model.getScheduleDate(),
      template_variables: model.getTemplateVariables(),
    };
    if (!triggerType) return base;
    return {
      ...base,
      trigger_type: triggerType,
      ...(model.getTriggerPlatform?.() ? { trigger_platform: model.getTriggerPlatform() } : {}),
      ...(model.getTriggerEvent?.() ? { trigger_event: model.getTriggerEvent() } : {}),
      ...(model.getVariablePaths?.() != null ? { variable_paths: model.getVariablePaths() } : {}),
      ...(model.getEnabled?.() != null ? { enabled: Boolean(model.getEnabled()) } : {}),
      // Event-triggered rows have no schedule/recipients; nullify so the server
      // short-circuit picks up.
      ...(isEventTriggered
        ? { schedule: null, send_now: false, selected_contacts: [], selected_groups: [], selected_tags: [] }
        : {}),
    };
  }
  async createBroadCastMessage(model) {
    const url = `/broadcast/${appConfig.getServiceId()}`;
    const { data } = await request.post(url, this.serialize(model));
    return data;
  }

  async updateBroadCastMessage(model) {
    const url = `/broadcast/${appConfig.getServiceId()}`;
    const { data } = await request.put(url, this.serialize(model));
    return data;
  }

  async getAllBroadcast() {
    const url = `/broadcast/${appConfig.getServiceId()}`;
    const { data } = await request.get(url);
    return data.map((model) => new Broadcast(this.parse(model)));

  }

  async deleteBroadCastMessage(broadcastId) {
    const url = `/broadcast/${appConfig.getServiceId()}/${broadcastId}`;
    const { data } = await request.delete(url);
    return data;
  }

  async getBroadcastReport(broadcastId, {
    runId = "",
    status = "all",
    search = "",
    limit = 50,
    offset = 0,
  } = {}) {
    const url = `/broadcast/${appConfig.getServiceId()}/${broadcastId}/report`;
    const { data } = await request.get(url, {
      params: {
        run_id: runId || undefined,
        status,
        search,
        limit,
        offset,
      },
    });
    return data;
  }

  async getBroadcastRuns(broadcastId) {
    const url = `/broadcast/${appConfig.getServiceId()}/${broadcastId}/report/runs`;
    const { data } = await request.get(url);
    return data;
  }

  async reconcileBroadcastReport(broadcastId) {
    const url = `/broadcast/${appConfig.getServiceId()}/${broadcastId}/report/reconcile`;
    const { data } = await request.post(url, {});
    return data;
  }
}

export default new BroadcastService();
